import type { ResizeHandle } from '@blocksuite/std/gfx';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';

import { aAlcaEhOAlvoDoEvento } from './tuninho/alca-e-o-alvo.js';

export enum HandleDirection {
  Bottom = 'bottom',
  BottomLeft = 'bottom-left',
  BottomRight = 'bottom-right',
  Left = 'left',
  Right = 'right',
  Top = 'top',
  TopLeft = 'top-left',
  TopRight = 'top-right',
}

function ResizeHandleRenderer(
  handle: ResizeHandle,
  rotatable: boolean,
  onPointerDown?: (e: PointerEvent, direction: ResizeHandle) => void,
  getCursor?: (options: {
    type: 'resize' | 'rotate';
    handle: ResizeHandle;
  }) => string
) {
  const handlerPointerDown = (e: PointerEvent) => {
    // tuninho (card 1864882251313972786): o 2º toque de um duplo toque, no CORPO do elemento,
    // chega a esta alça sem o ponto estar nela (medido: 9 px fora). Parar e redimensionar aqui
    // engolia o gesto — e o redimensionamento iniciado ainda deixaria a `activeInteraction` de
    // pé. Quando o toque não é da alça, ela deixa o evento seguir. Mouse e caneta: inalterados.
    if (!aAlcaEhOAlvoDoEvento(e)) return;
    e.stopPropagation();
    onPointerDown && onPointerDown(e, handle);
  };

  const rotationTpl =
    handle.length > 6 && rotatable
      ? html`<div
          class="rotate"
          style=${styleMap({
            cursor: getCursor
              ? getCursor({
                  type: 'rotate',
                  handle,
                })
              : 'default',
          })}
        ></div>`
      : nothing;

  return html`<div
    class="handle"
    aria-label=${handle}
    @pointerdown=${handlerPointerDown}
  >
    ${rotationTpl}
    <div
      class="resize transparent-handle"
      style=${styleMap({
        cursor: getCursor
          ? getCursor({
              type: 'resize',
              handle,
            })
          : 'default',
      })}
    ></div>
  </div>`;
}

/**
 * Indicate how selected elements can be resized.
 *
 * - edge: The selected elements can only be resized dragging edge, usually when note element is selected
 * - all: The selected elements can be resize both dragging edge or corner, usually when all elements are `shape`
 * - none: The selected elements can't be resized, usually when all elements are `connector`
 * - corner: The selected elements can only be resize dragging corner, this is by default mode
 * - edgeAndCorner: The selected elements can be resize both dragging left right edge or corner, usually when all elements are 'text'
 */
export type ResizeMode = 'edge' | 'all' | 'none' | 'corner' | 'edgeAndCorner';

export function RenderResizeHandles(
  resizeHandles: ResizeHandle[],
  rotatable: boolean,
  onPointerDown: (e: PointerEvent, direction: ResizeHandle) => void,
  getCursor?: (options: {
    type: 'resize' | 'rotate';
    handle: ResizeHandle;
  }) => string
) {
  return html`
    ${repeat(
      resizeHandles,
      handle => handle,
      handle =>
        ResizeHandleRenderer(handle, rotatable, onPointerDown, getCursor)
    )}
  `;
}
